
import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ItineraryEvent } from '@/types/itinerary';
import { Switch } from '@/components/ui/switch';
import { PlaneTakeoff, Train, Bus, Car, Ship } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface EventFormFieldsProps {
  formData: Partial<ItineraryEvent>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSwitchChange?: (name: string, checked: boolean) => void;
  handleSelectChange?: (name: string, value: string) => void;
}

const transportationTypes = [
  { value: 'flight', label: 'Flight', icon: <PlaneTakeoff className="h-4 w-4 mr-2" /> },
  { value: 'train', label: 'Train', icon: <Train className="h-4 w-4 mr-2" /> },
  { value: 'car', label: 'Car', icon: <Car className="h-4 w-4 mr-2" /> },
  { value: 'shuttle', label: 'Shuttle', icon: <Bus className="h-4 w-4 mr-2" /> },
  { value: 'ferry', label: 'Ferry/Ship', icon: <Ship className="h-4 w-4 mr-2" /> }
];

const EventFormFields: React.FC<EventFormFieldsProps> = ({ 
  formData, 
  handleChange,
  handleSwitchChange,
  handleSelectChange
}) => {
  return (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Label htmlFor="title">Title*</Label>
        <Input
          id="title"
          name="title"
          value={formData.title || ''}
          onChange={handleChange}
          placeholder="Event title"
          required
        />
      </div>
      
      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-2">
          <Label htmlFor="date">Date*</Label>
          <Input
            id="date"
            name="date"
            type="date"
            value={formData.date || ''}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="time">Start Time</Label>
            <Input
              id="time"
              name="time"
              type="time"
              value={formData.time || ''}
              onChange={handleChange}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="end_time">End Time</Label>
            <Input
              id="end_time"
              name="end_time"
              type="time"
              value={formData.end_time || ''}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="location">Location</Label>
        <Input
          id="location"
          name="location"
          value={formData.location || ''}
          onChange={handleChange}
          placeholder="Event location"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          name="description"
          value={formData.description || ''}
          onChange={handleChange}
          placeholder="Event details"
          rows={3}
        />
      </div>
      
      {handleSwitchChange && (
        <>
          <div className="flex items-center space-x-2">
            <Switch 
              id="isTransportation"
              checked={formData.isTransportation || false}
              onCheckedChange={(checked) => handleSwitchChange('isTransportation', checked)}
            />
            <Label htmlFor="isTransportation">
              Mark as transportation event
              <span className="text-xs text-muted-foreground block">
                Transportation events will appear in the travel schedule
              </span>
            </Label>
          </div>
          
          {formData.isTransportation && handleSelectChange && (
            <div className="space-y-2 pl-6 border-l-2 border-muted mt-2">
              <Label>Transportation Type</Label>
              <Select 
                value={formData.transportationType || 'flight'} 
                onValueChange={(value) => handleSelectChange('transportationType', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {transportationTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center">
                        {type.icon}
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EventFormFields;
